

import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { colors, fonts } from '../../shared/tokens';
import { Inner } from '../landingShared';

const A = '/images/redesign/landing';

const STAT_TILES = [
  { label: 'Total Switches', value: '24', valueColor: colors.ink, note: '● Active' },
  { label: 'Active Systems', value: '87', valueColor: colors.cyan, note: '● Online' },
  { label: 'Alerts', value: '3', valueColor: colors.red, note: '● Critical', noteColor: colors.red },
  { label: 'Efficiency', value: '94%', valueColor: colors.cyan, note: '● Optimal', noteColor: colors.cyan },
];

const ALERTS = [
  { text: 'High Temperature Detected — BET EAST · Heater Zone 2', time: '2m ago' },
  { text: 'Sensor Offline — Foxboro Station · Sensor B2', time: '15m ago' },
  { text: 'Wind Factor Out of Range — LASALLE Complex', time: '1h ago' },
];

const Hero = () => {
  const navigate = useNavigate();

  return (
    <Wrap>
      <Photo aria-hidden />
      <Gradient aria-hidden />
      <Inner $py={0}>
        <Grid>
          <Left>
            <Title>
              One Platform.
              <br />
              <em>Total Control.</em>
            </Title>
            <Lead>
              Umbrella OS unifies monitoring, control, and safety for industrial
              and transit environments — powering critical heating systems and
              operations at every scale.
            </Lead>
            <Actions>
              <ExploreBtn type="button">
                <img src={`${A}/hero-explore.svg`} alt="" width={14} height={16} />
                Explore Solutions
              </ExploreBtn>
              <LoginBtn type="button" onClick={() => navigate('/login')}>
                <img src={`${A}/hero-login.svg`} alt="" width={12} height={14} />
                Login
              </LoginBtn>
            </Actions>
            <TrustRow>
              <TrustLabel>Trusted by</TrustLabel>
              <TrustDivider />
              <TrustLogos>
                <span>BNSF Railway</span>
                <span>SNC-Lavalin</span>
                <span>WSP</span>
                <span>ARUP</span>
              </TrustLogos>
            </TrustRow>
          </Left>

          <Mock>
            <MockHeader>
              <Dots>
                <Dot $c={colors.cyan} />
                <Dot $c="#f0a500" />
                <Dot $c={colors.red} />
              </Dots>
              <MockTitle>SYSTEM OVERVIEW — BET EAST</MockTitle>
              <Live>
                <LiveDot />
                LIVE
              </Live>
            </MockHeader>

            <MockBody>
              <Tiles>
                {STAT_TILES.map((t) => (
                  <Tile key={t.label}>
                    <TileLabel>{t.label}</TileLabel>
                    <TileValue style={{ color: t.valueColor }}>{t.value}</TileValue>
                    <TileNote style={{ color: t.noteColor || colors.green }}>
                      {t.note}
                    </TileNote>
                  </Tile>
                ))}
              </Tiles>

              <Chart>
                <ChartYAxis>
                  <span>400</span>
                  <span>200</span>
                  <span>0</span>
                </ChartYAxis>
                <ChartPlot>
                  <Baseline aria-hidden />
                  <Line src={`${A}/chart-line1.svg`} alt="" style={{ top: '18%' }} />
                  <Line src={`${A}/chart-line2.svg`} alt="" style={{ top: '48%' }} />
                  <Line src={`${A}/chart-line3.svg`} alt="" style={{ top: '72%' }} />
                  <Temp>59°C</Temp>
                </ChartPlot>
                <ChartXAxis>
                  <span>115</span>
                  <span>120</span>
                  <span>125</span>
                  <span>130</span>
                  <span>135</span>
                </ChartXAxis>
              </Chart>

              <AlertsBox>
                <AlertsTitle>Recent Alerts</AlertsTitle>
                {ALERTS.map((a, i) => (
                  <AlertRow key={i} $last={i === ALERTS.length - 1}>
                    <AlertDot />
                    <AlertText>{a.text}</AlertText>
                    <AlertTime>{a.time}</AlertTime>
                    <AlertTag>INFO</AlertTag>
                  </AlertRow>
                ))}
              </AlertsBox>
            </MockBody>
          </Mock>
        </Grid>
      </Inner>
    </Wrap>
  );
};

export default Hero;



const Wrap = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  background: ${colors.bg};
`;

const Photo = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${A}/hero.png);
  background-size: cover;
  background-position: right center;
  z-index: 0;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.75) 46%,
    rgba(153, 153, 153, 0) 99.8%
  );
`;

const Grid = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 760px;
  padding: 90px 0;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 48px;
    min-height: 0;
  }
`;

const Left = styled.div`
  width: 418px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 480px;
  }
`;

const Title = styled.h1`
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 100px;
  line-height: 90px;
  letter-spacing: -1px;
  text-transform: uppercase;
  color: ${colors.ink};

  em {
    font-style: normal;
    color: ${colors.cyan};
  }

  @media (max-width: 600px) {
    font-size: 64px;
    line-height: 58px;
  }
`;

const Lead = styled.p`
  margin-top: 28px;
  max-width: 480px;
  padding-left: 21.5px;
  border-left: 1.5px solid #1e8a93;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 17px;
  line-height: 29.75px;
  color: ${colors.textSoft};
`;

const Actions = styled.div`
  margin-top: 44px;
  display: flex;
  gap: 14px;

  @media (max-width: 400px) {
    flex-direction: column;
  }
`;

const baseBtn = `
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border-radius: 3px;
  font-family: ${fonts.condensed};
  font-size: 15px;
  line-height: 22.5px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
`;

const ExploreBtn = styled.button`
  ${baseBtn}
  padding: 16px 36px;
  background: ${colors.cyan};
  color: ${colors.bg};
  font-weight: 800;
  transition: background 0.15s ease;
  &:hover { background: #4ad6e2; }
`;

const LoginBtn = styled.button`
  ${baseBtn}
  padding: 15.75px 32.75px;
  background: transparent;
  border: 0.75px solid rgba(255, 255, 255, 0.22);
  color: ${colors.ink};
  font-weight: 700;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover { border-color: ${colors.cyan}; background: rgba(56, 200, 212, 0.06); }
`;

const TrustRow = styled.div`
  margin-top: 52px;
  display: flex;
  align-items: center;
  gap: 22px;
`;

const TrustLabel = styled.span`
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 11px;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${colors.muted2};
  white-space: nowrap;
`;

const TrustDivider = styled.span`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.07);
`;

const TrustLogos = styled.div`
  display: flex;
  gap: 28px;

  span {
    font-family: ${fonts.condensed};
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 1.56px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.28);
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 12px 20px;
  }
`;



const Mock = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 418px;
  height: 453px;
  display: flex;
  flex-direction: column;
  border: 0.75px solid rgba(56, 200, 212, 0.14);
  border-radius: 10px;
  background: #0d1420;
  overflow: hidden;
  box-shadow: 0px 0px 0px 1px rgba(56, 200, 212, 0.05),
    0px 40px 80px 0px rgba(0, 0, 0, 0.7), 0px 0px 80px 0px rgba(56, 200, 212, 0.04);

  @media (max-width: 1024px) {
    position: relative;
    top: auto;
    transform: none;
    width: 100%;
    max-width: 418px;
  }
`;

const MockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 10.75px;
  background: rgba(17, 27, 46, 0.95);
  border-bottom: 0.75px solid rgba(255, 255, 255, 0.07);
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 4.5px;
  background: ${({ $c }) => $c};
`;

const MockTitle = styled.p`
  font-family: ${fonts.mono};
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 0.8px;
  color: ${colors.muted};
`;

const Live = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: ${fonts.mono};
  font-size: 9px;
  letter-spacing: 0.54px;
  color: ${colors.green};
`;

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.5); opacity: 1; }
  70%  { box-shadow: 0 0 0 5px rgba(74, 222, 128, 0); opacity: 0.7; }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); opacity: 1; }
`;

const LiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: ${colors.green};
  animation: ${pulse} 1.8s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MockBody = styled.div`
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const Tile = styled.div`
  padding: 10.75px 8px;
  border: 0.75px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  background: #111b2e;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TileLabel = styled.p`
  font-family: ${fonts.body};
  font-size: 9px;
  line-height: 13.5px;
  letter-spacing: 0.54px;
  text-transform: uppercase;
  color: ${colors.muted2};
`;

const TileValue = styled.p`
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 26px;
  line-height: 26px;
`;

const TileNote = styled.p`
  font-family: ${fonts.body};
  font-size: 9px;
  line-height: 13.5px;
`;

const Chart = styled.div`
  position: relative;
  margin-top: 12px;
  height: 108px;
  display: grid;
  grid-template-columns: 24px 1fr;
  grid-template-rows: 1fr 12px;
  column-gap: 6px;
`;

const ChartYAxis = styled.div`
  grid-row: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding: 2px 0;
  span {
    font-family: 'Consolas', ${fonts.mono};
    font-size: 6.75px;
    color: #f97316;
  }
`;

const ChartPlot = styled.div`
  grid-row: 1;
  grid-column: 2;
  position: relative;
  overflow: hidden;
`;

const Baseline = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 68%;
  height: 0.7px;
  background: rgba(255, 255, 255, 0.12);
`;

const Line = styled.img`
  position: absolute;
  left: 0;
  width: 100%;
  height: auto;
`;

const Temp = styled.span`
  position: absolute;
  right: 0;
  top: 62%;
  font-family: 'Consolas', ${fonts.mono};
  font-weight: 700;
  font-size: 7.4px;
  color: #5b9bd5;
`;

const ChartXAxis = styled.div`
  grid-row: 2;
  grid-column: 2;
  display: flex;
  justify-content: space-between;
  span {
    font-family: 'Consolas', ${fonts.mono};
    font-size: 6px;
    color: #666;
  }
`;

const AlertsBox = styled.div`
  margin-top: 12px;
  padding: 12.75px;
  border: 0.75px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  background: #111b2e;
`;

const AlertsTitle = styled.p`
  font-family: ${fonts.body};
  font-size: 9px;
  line-height: 13.5px;
  letter-spacing: 0.54px;
  text-transform: uppercase;
  color: ${colors.muted};
  margin-bottom: 8px;
`;

const AlertRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0 5.75px;
  ${({ $last }) => !$last && `border-bottom: 0.75px solid rgba(255, 255, 255, 0.07);`}
`;

const AlertDot = styled.span`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: ${colors.cyan};
`;

const AlertText = styled.p`
  flex: 1;
  min-width: 0;
  font-family: ${fonts.body};
  font-size: 9px;
  line-height: 12.6px;
  color: ${colors.textSoft};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlertTime = styled.span`
  flex-shrink: 0;
  font-family: ${fonts.mono};
  font-size: 8px;
  color: ${colors.muted2};
`;

const AlertTag = styled.span`
  flex-shrink: 0;
  padding: 2px 7px;
  border-radius: 2px;
  background: rgba(56, 200, 212, 0.12);
  font-family: ${fonts.mono};
  font-weight: 700;
  font-size: 8px;
  color: ${colors.cyan};
`;
